import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type {
	useRecentJiraProjectAndIssueQuery$data,
	useRecentJiraProjectAndIssueQuery$variables,
} from './__generated__/useRecentJiraProjectAndIssueQuery.graphql.ts';

const RecentJiraProjectAndIssueQuery = graphql`
	query useRecentJiraProjectAndIssueQuery($cloudId: ID!, $jql: String!) {
		jira {
			recentItems(
				cloudId: $cloudId
				types: [ISSUE]
				filter: { includeArchivedProjects: false }
				first: 1
			) {
				edges {
					node {
						... on JiraIssue {
							key
							summary
							webUrl
							issueType {
								name
							}
							projectField {
								project {
									key
									name
									webUrl
								}
							}
						}
					}
				}
			}
			issueSearch(cloudId: $cloudId, issueSearchInput: { jql: $jql }, first: 1)
				@optIn(to: "JiraSpreadsheetComponent-M1") {
				edges {
					node {
						key
						summary
						webUrl
						issueType {
							name
						}
						projectField {
							project {
								key
								name
								webUrl
							}
						}
					}
				}
			}
		}
	}
`;

type UseRecentJiraProjectAndIssueProps = {
	skip?: boolean;
};

export const useRecentJiraProjectAndIssue = ({
	skip,
}: UseRecentJiraProjectAndIssueProps): {
	data: {
		issue: {
			key: string | null;
			summary: string | null;
			webUrl: string | null;
			issueTypeName: string | null;
		};
		hasFullData: boolean;
		project: {
			key: string | null;
			name: string | null;
			webUrl: string | null;
		};
	};
	isLoading: boolean;
} => {
	const { cloudId } = useSessionData();
	const { data, loading } = useQueryInterop<
		useRecentJiraProjectAndIssueQuery$data,
		useRecentJiraProjectAndIssueQuery$variables
	>(RecentJiraProjectAndIssueQuery, {
		variables: {
			cloudId,
			jql: '(assignee = currentUser() OR creator = currentUser()) AND statusCategory != Done ORDER BY updated DESC',
		},
		skip: !cloudId || skip,
		interopApolloAggClient: true,
		onError: (err) => {
			markErrorAsHandled(err);
		},
	});

	// Try to use issueSearch first, and fall back to recentItems if issueSearch doesn't return any results (e.g. if user has no issues)
	const recentIssue =
		data?.jira?.issueSearch?.edges?.[0]?.node ?? data?.jira?.recentItems?.edges?.[0]?.node;

	const issue = {
		key: recentIssue?.key ?? null,
		summary: recentIssue?.summary ?? null,
		webUrl: recentIssue?.webUrl ?? null,
		issueTypeName: recentIssue?.issueType?.name ?? null,
	};
	const project = {
		key: recentIssue?.projectField?.project?.key ?? null,
		name: recentIssue?.projectField?.project?.name ?? null,
		webUrl: recentIssue?.projectField?.project?.webUrl ?? null,
	};

	return {
		isLoading: loading,
		data: {
			issue,
			project,
			hasFullData: !!(
				issue.key &&
				issue.summary &&
				issue.webUrl &&
				issue.issueTypeName &&
				project.key &&
				project.name &&
				project.webUrl
			),
		},
	};
};
